/**
 * ROBOT BRIEF DETAY SAYFASI — İçerik Robotu önerisine tıklanınca açılır:
 * varyasyon kelime tablosu, semantik set, plan, arama niyeti, kanibalizasyon,
 * AI/GEO açısı, link hedefleri + takvim/yazar aksiyonları. (Liste sayfası sade kalır.)
 */
import { useState } from "react";
import { Link, useRoute, useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { GeriButon } from "@/components/geri-buton";
import { HacimRozeti } from "@/components/hacim-rozeti";
import { useTakvimdeVar } from "@/hooks/use-takvim";
import { ArrowLeft, Target, Trophy, Check, CalendarPlus } from "lucide-react";

const C = { cyan: "#e83e3c", green: "#12b76a", red: "#f04438", amber: "#f79009", purple: "#e83e3c", blue: "#ec6461" };
const fmt = (n: number) => (n >= 10000 ? `${(n / 1000).toFixed(0)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n)));
const shortUrl = (u: string) => (u || "").replace(/^https?:\/\/[^/]+/, "").replace(/\/$/, "") || "/";

type Brief = {
  title: string; targetKeyword: string; volume: number; globalVolume?: number; difficulty: number;
  supportingKeywords: string[]; clusterName: string | null; isNewCluster?: boolean;
  contentKind?: string; intentKind?: string; priority: string; feasibility: string;
  rationale: string; searchIntent: string; aiAngle: string;
  outline: { h2: string; h3: string[] }[];
  internalLinkTargets: { anchor: string; url: string }[];
  competitorEvidence?: { url: string; traffic: number } | null;
  cannibalizationCheck?: { verdict: string; note: string; closestExisting: string | null } | null;
  variationMetrics?: { keyword: string; role: string; volume: number; difficulty: number; cpc: number; competition: number; cluster: string | null }[];
  semanticKeywords?: { term: string; type: string }[];
  deepDive?: DeepDive | null;
};
// 14 bölümlü derin analiz (backend BriefDeepDive ile aynı şekil)
type DeepDive = {
  generatedAt: string; cost: number;
  titleMetaBoost: { title: string; metaDescription: string; angle: string }[];
  competitorTraffic: { url: string; domain: string; title: string; traffic: number; keywords: number }[];
  aiLandscape: { hasAiOverview: boolean; aiSources: string[]; note: string };
  aiOverviewAnswer: { answer: string; sources: string[]; weAreCited: boolean };
  strengthenPlan: { action: string; priority: string; why: string }[];
  linkWarnings: string[];
  incomingLinkOpportunities: { url: string; title: string | null; ilr: number | null; suggestedAnchor: string }[];
  keywords: { keyword: string; volume: number; role: string }[];
  organicCompetitors: { domain: string; traffic: number; keywords: number }[];
  keywordOpportunities: { keyword: string; volume: number; difficulty: number }[];
  clusterContext: { clusterName: string | null; pageCount: number; pillarUrl: string | null; memberSamples: { url: string; title: string }[] };
  geoTargetPrompts: string[];
  promptActions: { prompt: string; action: string }[];
};
type PlannerResp = { generatedAt: string | null; data: { briefs: Brief[]; meta: { market: string } } | null };

const prioLabel: Record<string, { l: string; c: string }> = { high: { l: "Yüksek", c: C.red }, medium: { l: "Orta", c: C.amber }, low: { l: "Düşük", c: C.green } };

export default function PlannerBrief() {
  const [, params] = useRoute("/planner-brief/:kw");
  const [, setLocation] = useLocation();
  const kw = decodeURIComponent(params?.kw ?? "");
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  // "Eklendi" durumu SUNUCUDAN okunur. Eskiden sadece bu iki useState vardı; sayfa
  // yenilenince sıfırlanıyor ve zaten takvimde olan brief için buton yeniden
  // "İçerik Takvimine Ekle" diyordu (kullanıcı: "sayfayı yenileyince eklendi demiyor").
  const [eklendiYerel, setEklendiYerel] = useState(false);
  const [gonderildiYerel, setGonderildiYerel] = useState(false);

  // Brief'i HANGİ koşuda olursa olsun getirir. Eskiden burada son koşu çekilip
  // içinde kelime aranıyordu; eski bir koşunun brief'i açıldığında liste onu
  // içermediği için sayfa bomboş açılıyordu ("eski brieflerin detayı gözükmüyor").
  const { data: tekBrief } = useQuery({
    queryKey: ["planner-brief", activeDomainId, kw],
    enabled: !!activeDomainId && !!kw,
    queryFn: async (): Promise<{ brief: Brief | null; runId: number | null; market: string | null }> => {
      try {
        const r = await fetch(`/api/domains/${activeDomainId}/planner-brief?targetKeyword=${encodeURIComponent(kw)}`);
        return r.ok ? await r.json() : { brief: null, runId: null, market: null };
      } catch { return { brief: null, runId: null, market: null }; }
    },
  });
  // Yazar havuzu fallback'i: brief Üretim Ajanı'na gönderilmiş ama planner koşusundan
  // silinmiş/eskimiş olabilir — detay sayfası HER İKİ kaynaktan da bulur.
  const { data: writerPool } = useQuery({
    queryKey: ["writer-briefs", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async (): Promise<{ briefs: { id: number; brief: Brief }[] }> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/writer-briefs`); return r.ok ? await r.json() : { briefs: [] }; } catch { return { briefs: [] }; }
    },
  });
  // İçerik takvimi durumu ortak hook'tan (üç sayfa aynı kuralı paylaşır).
  const takvimdeMi = useTakvimdeVar(activeDomainId);
  const takvimdeVar = takvimdeMi(kw);

  const plannerBrief = tekBrief?.brief ?? null;
  const writerBrief = (writerPool?.briefs ?? []).find((w) => w.brief?.targetKeyword === kw)?.brief ?? null;
  const b = plannerBrief ?? writerBrief;
  const added = eklendiYerel || takvimdeVar;
  const sent = gonderildiYerel || !!writerBrief; // yazar havuzunda kaydı varsa gönderilmiştir
  // Derin analiz yalnız planner kaydı varken üretilebilir (sonuç oraya yazılır);
  // yazar-havuzu kopyasında hazır deepDive varsa yine gösterilir.
  const canGenerateDeep = !!plannerBrief;

  // ── BRIEF DÜZENLEME + ONAY (insan-döngüde kontrol) ──
  const [editOpen, setEditOpen] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState<string | null>(null);
  const [eTitle, setETitle] = useState("");
  const [eSupport, setESupport] = useState("");
  const [eOutline, setEOutline] = useState("");
  const openEdit = () => {
    if (!b) return;
    setETitle(b.title);
    setESupport((b.supportingKeywords ?? []).join(", "));
    setEOutline(b.outline.map((o) => `## ${o.h2}${o.h3.length ? "\n" + o.h3.map((h) => `- ${h}`).join("\n") : ""}`).join("\n"));
    setEditOpen(true); setEditErr(null);
  };
  const putBrief = async (patch: Record<string, unknown>) => {
    const r = await fetch(`/api/domains/${activeDomainId}/content-planner/brief`, {
      method: "PUT", headers: { "content-type": "application/json" },
      body: JSON.stringify({ targetKeyword: kw, patch }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error ?? "Kaydedilemedi");
    queryClient.invalidateQueries({ queryKey: ["content-planner", activeDomainId] });
    queryClient.invalidateQueries({ queryKey: ["planner-brief", activeDomainId, kw] });
    queryClient.invalidateQueries({ queryKey: ["writer-briefs", activeDomainId] });
  };
  const saveEdit = async () => {
    setEditBusy(true); setEditErr(null);
    try {
      // Plan metnini outline yapısına çevir: "## H2" satırı bölüm açar, "- x" alt başlık
      const outline: { h2: string; h3: string[] }[] = [];
      for (const line of eOutline.split("\n")) {
        const t = line.trim();
        if (t.startsWith("## ")) outline.push({ h2: t.slice(3).trim(), h3: [] });
        else if ((t.startsWith("- ") || t.startsWith("* ")) && outline.length > 0) outline[outline.length - 1].h3.push(t.slice(2).trim());
        else if (t && outline.length === 0) outline.push({ h2: t, h3: [] });
      }
      await putBrief({
        title: eTitle,
        supportingKeywords: eSupport.split(",").map((s) => s.trim()).filter(Boolean),
        outline,
      });
      setEditOpen(false);
    } catch (e) { setEditErr((e as Error).message); } finally { setEditBusy(false); }
  };
  // ── REVİZYON: tek brief'i yeniden ürettir (madde 10 + 11) ──
  // Kelime değişirse URL de değişir; sayfayı yeni kelimeye taşırız yoksa kullanıcı
  // artık var olmayan bir kelimenin sayfasında kalır.
  const [revOpen, setRevOpen] = useState(false);
  const [revKw, setRevKw] = useState("");
  const [revBusy, setRevBusy] = useState(false);
  const [revErr, setRevErr] = useState<string | null>(null);
  const [revOk, setRevOk] = useState<string | null>(null);
  const revizeEt = async () => {
    if (!activeDomainId) return;
    setRevBusy(true); setRevErr(null); setRevOk(null);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/planner-brief/regenerate`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ targetKeyword: kw, newKeyword: revKw.trim(), runId: tekBrief?.runId ?? undefined }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error ?? "Yeniden üretilemedi");
      queryClient.invalidateQueries({ queryKey: ["planner-briefs", activeDomainId] });
      queryClient.invalidateQueries({ queryKey: ["content-planner", activeDomainId] });
      queryClient.invalidateQueries({ queryKey: ["writer-briefs", activeDomainId] });
      const yeniKw = d.brief?.targetKeyword ?? kw;
      if (yeniKw !== kw) { setLocation(`/planner-brief/${encodeURIComponent(yeniKw)}`); return; }
      queryClient.invalidateQueries({ queryKey: ["planner-brief", activeDomainId, kw] });
      setRevOk(d.kanibalUyari ? `Yeniden üretildi. ⚠️ ${d.kanibalUyari}` : "Yeniden üretildi.");
      setRevOpen(false);
    } catch (e) { setRevErr((e as Error).message); } finally { setRevBusy(false); }
  };

  const toggleApprove = async () => {
    if (!b) return;
    try { await putBrief({ approved: !(b as { approved?: boolean }).approved }); } catch { /* sessiz */ }
  };

  // ── 14 bölümlü DERİN ANALİZ: başlat + yokla; bitince content-planner yenilenir ──
  const queryClient = useQueryClient();
  const [deepStarted, setDeepStarted] = useState(false);
  const [deepErr, setDeepErr] = useState<string | null>(null);
  const { data: deepStatus } = useQuery({
    queryKey: ["brief-deep-status", activeDomainId, kw],
    enabled: !!activeDomainId && deepStarted,
    refetchInterval: (q) => ((q.state.data as { running?: boolean } | undefined)?.running ? 4000 : false),
    queryFn: async (): Promise<{ running: boolean; error: string | null }> => {
      const r = await fetch(`/api/domains/${activeDomainId}/planner-brief-deep-status?targetKeyword=${encodeURIComponent(kw)}`);
      const d = r.ok ? await r.json() : { running: false, error: null };
      if (!d.running) {
        queryClient.invalidateQueries({ queryKey: ["content-planner", activeDomainId] });
    queryClient.invalidateQueries({ queryKey: ["planner-brief", activeDomainId, kw] });
        if (d.error) setDeepErr(d.error);
      }
      return d;
    },
  });
  const deepRunning = deepStarted && (deepStatus?.running ?? true) && !b?.deepDive;
  const startDeepDive = async () => {
    setDeepErr(null);
    const r = await fetch(`/api/domains/${activeDomainId}/planner-brief-deep`, {
      method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ targetKeyword: kw }),
    });
    if (r.ok || r.status === 409) setDeepStarted(true);
    else setDeepErr((await r.json().catch(() => ({}))).error ?? "Başlatılamadı");
  };
  const dd = b?.deepDive ?? null;

  const addToCalendar = async () => {
    if (!activeDomainId || !b) return;
    const outlineTxt = b.outline.map((o) => `## ${o.h2}${o.h3.length ? "\n" + o.h3.map((h) => `  - ${h}`).join("\n") : ""}`).join("\n");
    await fetch(`/api/domains/${activeDomainId}/content-plans`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ title: b.title, plannedDate: new Date().toISOString().slice(0, 10), contentType: "blog", notes: `${b.rationale}\n\n${outlineTxt}`, keywords: [{ keyword: b.targetKeyword, usa: 0, global: b.volume, role: "ana" }, ...b.supportingKeywords.map((k) => ({ keyword: k, usa: 0, global: 0, role: "destek" }))] }),
    });
    setEklendiYerel(true);
    queryClient.invalidateQueries({ queryKey: ["content-plans", activeDomainId] });
  };
  const sendToWriter = async () => {
    if (!activeDomainId || !b) return;
    const r = await fetch(`/api/domains/${activeDomainId}/writer-briefs`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ brief: b }) });
    if (r.ok || r.status === 409) {
      setGonderildiYerel(true);
      queryClient.invalidateQueries({ queryKey: ["writer-briefs", activeDomainId] });
    }
  };

  return (
    <div className="px-4 sm:px-6 py-6 w-full">
      <GeriButon yedekHref="/content-planner" yedekEtiket="Brief Keşif Ajanı" className="mb-4" />

      {!b && <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">Brief bulunamadı — İçerik Robotu sayfasından tekrar seç.</CardContent></Card>}

      {b && (<>
        <div className="flex items-center gap-2 flex-wrap mb-2 text-[11px]">
          <span className="px-2 py-0.5 rounded-full" style={{ background: `${(prioLabel[b.priority] ?? prioLabel.medium).c}22`, color: (prioLabel[b.priority] ?? prioLabel.medium).c }}>● {(prioLabel[b.priority] ?? prioLabel.medium).l} öncelik</span>
          {b.contentKind === "evergreen" ? <span className="px-2 py-0.5 rounded-full" style={{ background: `${C.green}18`, color: C.green }}>🌲 Evergreen</span>
            : b.clusterName && <span className="px-2 py-0.5 rounded-full" style={{ background: "rgba(0,0,0,0.05)", color: "#70706c" }}>{b.isNewCluster ? "🆕 " : ""}{b.clusterName}</span>}
          {b.intentKind && <span className="px-2 py-0.5 rounded-full" style={b.intentKind === "ticari" ? { background: "rgba(59,130,246,0.12)", color: "#b12220" } : { background: "rgba(139,92,246,0.12)", color: "#d42b29" }}>{b.intentKind === "ticari" ? "💼 Ticari" : "📖 Bilgi"}</span>}
          {b.cannibalizationCheck?.verdict === "temiz" && <span className="px-2 py-0.5 rounded-full" style={{ background: "rgba(34,197,94,0.1)", color: "#039855" }}>🛡️ Kanibalizasyon temiz</span>}
        </div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <h1 className="text-xl font-bold leading-snug flex-1 min-w-0">{b.title}</h1>
          <div className="text-right shrink-0">
            <HacimRozeti volume={b.volume} globalVolume={b.globalVolume} difficulty={b.difficulty} buyuk />
          </div>
        </div>
        <div className="text-xs mt-1"><Target className="w-3 h-3 inline mr-1" style={{ color: C.amber }} /><b style={{ color: C.amber }}>{b.targetKeyword}</b>{b.supportingKeywords.length > 0 && <span className="text-muted-foreground"> + {b.supportingKeywords.join(", ")}</span>}</div>
        <p className="text-sm text-muted-foreground mt-2">{b.rationale}</p>
        {b.competitorEvidence && (
          <a href={b.competitorEvidence.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs mt-1.5" style={{ color: C.red }}>
            <Trophy className="w-3 h-3" /> Rakip kanıtı: {shortUrl(b.competitorEvidence.url)} ({fmt(b.competitorEvidence.traffic)}/ay trafik)
          </a>
        )}

        <div className="flex items-center gap-2 my-4 flex-wrap">
          <Button size="sm" variant={added ? "outline" : "secondary"} disabled={added} onClick={addToCalendar} className="gap-1.5">
            {added ? <><Check className="w-3.5 h-3.5" style={{ color: C.green }} /> Takvime eklendi</> : <><CalendarPlus className="w-3.5 h-3.5" /> İçerik Takvimine Ekle</>}
          </Button>
          <Button size="sm" variant={sent ? "outline" : "secondary"} disabled={sent} onClick={sendToWriter} className="gap-1.5">
            {sent ? <><Check className="w-3.5 h-3.5" style={{ color: C.green }} /> Ajan havuzunda</> : <>✍️ Üretim Ajanına Gönder</>}
          </Button>
          {/* İNSAN-DÖNGÜDE KONTROL: brief düzenlenir, onaylanır, SONRA üretime gider */}
          <Button size="sm" variant="outline" onClick={openEdit} className="gap-1.5" style={{ borderColor: "rgba(139,92,246,0.5)", color: "#d42b29" }}>
            ✏️ Brief'i Düzenle
          </Button>
          <Button size="sm" variant="outline" onClick={toggleApprove} className="gap-1.5"
            style={(b as { approved?: boolean }).approved ? { borderColor: C.green, color: "#039855", background: "rgba(34,197,94,0.08)" } : { borderColor: "rgba(0,0,0,0.15)", color: "#70706c" }}>
            {(b as { approved?: boolean }).approved ? "✅ Onaylı" : "☐ Brief'i Onayla"}
          </Button>
          {/* REVİZYON: brief'i (istenirse farklı bir hedef kelimeyle) yeniden ürettirir.
              Tüm motoru değil, yalnız bu brief'i — saniyeler sürer, ~$0.02. */}
          {plannerBrief && (
            <Button size="sm" variant="outline" onClick={() => { setRevOpen((v) => !v); setRevKw(kw); setRevErr(null); setRevOk(null); }} className="gap-1.5">
              🔄 Revize Et
            </Button>
          )}
        </div>

        {revOpen && plannerBrief && (
          <Card className="mb-4 border-brand-500/40"><CardContent className="p-4 space-y-3">
            <div className="text-xs font-semibold text-brand-600 dark:text-brand-400">
              🔄 Brief'i Yeniden Üret — hedef kelimeyi değiştirebilirsin
            </div>
            <p className="text-[11px] text-muted-foreground">
              Sadece bu brief yeniden yazılır (başlık, destek kelimeleri, semantik set, plan, iç linkler, GEO açısı).
              Motorun tamamı çalışmaz. Kelimeyi değiştirirsen hacim ve zorluk yeniden ölçülür, onay işareti kalkar.
            </p>
            <div>
              <div className="text-[11px] text-muted-foreground mb-1">Hedef kelime</div>
              <input value={revKw} onChange={(e) => setRevKw(e.target.value)}
                className="w-full text-sm px-3 py-2 rounded-lg border bg-background" placeholder="ör. hormone check-up in turkey" />
            </div>
            {revErr && <div className="text-[12px] text-red-600 dark:text-red-400">{revErr}</div>}
            {revOk && <div className="text-[12px] text-success-700 dark:text-success-400">{revOk}</div>}
            <div className="flex gap-2">
              <Button size="sm" onClick={revizeEt} disabled={revBusy || revKw.trim().length < 3}>
                {revBusy ? "Yeniden üretiliyor…" : "Yeniden Üret"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setRevOpen(false)} disabled={revBusy}>Vazgeç</Button>
            </div>
          </CardContent></Card>
        )}

        {editOpen && (
          <Card className="mb-4" style={{ borderColor: "rgba(139,92,246,0.4)" }}><CardContent className="p-4 space-y-3">
            <div className="text-xs font-semibold text-brand-600 dark:text-brand-400">✏️ Brief Düzenleme — kaydettiğinde üretim havuzundaki kopya da güncellenir</div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wide mb-1 text-brand-500">Başlık</div>
              <input value={eTitle} onChange={(ev) => setETitle(ev.target.value)} className="text-sm w-full px-3 py-2 rounded-lg border outline-none" style={{ borderColor: "rgba(0,0,0,0.1)" }} />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wide mb-1 text-brand-500">Destek Kelimeler (virgülle)</div>
              <input value={eSupport} onChange={(ev) => setESupport(ev.target.value)} className="text-sm w-full px-3 py-2 rounded-lg border outline-none" style={{ borderColor: "rgba(0,0,0,0.1)" }} />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wide mb-1 text-brand-500">Plan (satır başı "## " = H2, "- " = alt başlık)</div>
              <textarea value={eOutline} onChange={(ev) => setEOutline(ev.target.value)} rows={10} className="text-sm w-full px-3 py-2 rounded-lg border outline-none resize-y font-mono" style={{ borderColor: "rgba(0,0,0,0.1)" }} />
            </div>
            <div className="flex items-center gap-3">
              <Button size="sm" onClick={saveEdit} disabled={editBusy} style={{ background: "#d42b29", color: "#fff" }}>{editBusy ? "Kaydediliyor…" : "Kaydet"}</Button>
              <button onClick={() => setEditOpen(false)} className="text-xs text-muted-foreground hover:underline">Vazgeç</button>
              {editErr && <span className="text-xs text-red-600">{editErr}</span>}
            </div>
          </CardContent></Card>
        )}

        {/* Varyasyon kelime tablosu */}
        {(b.variationMetrics?.length ?? 0) > 0 && (
          <Card className="mb-4"><CardContent className="p-4">
            <div className="text-[11px] font-semibold mb-1.5 text-blue-light-700 dark:text-blue-light-500">📊 Varyasyon Kelime Tablosu <span className="text-muted-foreground font-normal">(hedeflenecek kelimeler — gerçek veriler)</span></div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[620px]">
                <thead><tr className="text-[10px] text-muted-foreground text-left border-b" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
                  <th className="py-1 pr-3">Kelime</th><th className="py-1 pr-2">Rol</th>
                  <th className="py-1 pr-2 text-right">Aylık Hacim</th><th className="py-1 pr-2 text-right">Rekabet (KD)</th>
                  <th className="py-1 pr-2 text-right">CPC</th><th className="py-1 pr-2 text-right">Reklam Rek.</th><th className="py-1">Cluster</th>
                </tr></thead>
                <tbody>{b.variationMetrics!.map((v, i) => {
                  const kdColor = v.difficulty >= 60 ? C.red : v.difficulty >= 30 ? C.amber : C.green;
                  const roleStyle = v.role === "ana" ? { background: "rgba(245,158,11,0.15)", color: "#b54708" } : v.role === "varyasyon" ? { background: "rgba(6,182,212,0.1)", color: "#b12220" } : { background: "rgba(0,0,0,0.05)", color: "#70706c" };
                  return (
                    <tr key={i} className="border-b last:border-0" style={{ borderColor: "rgba(0,0,0,0.04)" }}>
                      <td className="py-1 pr-3 font-medium">{v.keyword}</td>
                      <td className="py-1 pr-2"><span className="text-[10px] px-1.5 py-0.5 rounded" style={roleStyle}>{v.role}</span></td>
                      <td className="py-1 pr-2 text-right tabular-nums" style={{ color: v.volume > 0 ? C.green : "#a3a3a0" }} title={v.volume > 0 ? "" : "Bu ifade için hacim verisi yok — sıfır arama demek değil"}>{v.volume > 0 ? fmt(v.volume) : "—"}</td>
                      <td className="py-1 pr-2 text-right tabular-nums" style={{ color: v.difficulty > 0 ? kdColor : "#a3a3a0" }}>{v.difficulty > 0 ? v.difficulty : "—"}</td>
                      <td className="py-1 pr-2 text-right tabular-nums" style={{ color: v.cpc > 0 ? "#d42b29" : "#a3a3a0" }}>{v.cpc > 0 ? `$${v.cpc.toFixed(2)}` : "—"}</td>
                      <td className="py-1 pr-2 text-right tabular-nums text-muted-foreground">{v.competition > 0 ? v.competition : "—"}</td>
                      <td className="py-1 text-muted-foreground">{v.cluster ? `${b.isNewCluster ? "🆕 " : ""}${v.cluster}` : b.contentKind === "evergreen" ? "🌲 Evergreen" : "—"}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          </CardContent></Card>
        )}

        {/* Semantik set */}
        {(b.semanticKeywords?.filter((s) => s.type !== "varyasyon").length ?? 0) > 0 && (
          <Card className="mb-4"><CardContent className="p-4">
            <div className="text-[11px] font-semibold mb-1.5 text-brand-600 dark:text-brand-400">🧬 Semantik SEO Seti <span className="text-muted-foreground font-normal">(içerikte işlenmesi gerekenler)</span></div>
            <div className="space-y-1.5">
              {([["soru", "Sorular", "#b54708", "rgba(245,158,11,0.1)"], ["varlık", "Varlıklar (Entity)", "#b12220", "rgba(59,130,246,0.1)"], ["alt-konu", "Alt Konular", "#039855", "rgba(34,197,94,0.1)"]] as const).map(([type, label, color, bg]) => {
                const items = (b.semanticKeywords ?? []).filter((s) => s.type === type);
                if (items.length === 0) return null;
                return (
                  <div key={type} className="flex items-start gap-2 flex-wrap">
                    <span className="text-[10px] font-semibold text-muted-foreground w-[110px] shrink-0 pt-0.5">{label}</span>
                    <div className="flex flex-wrap gap-1 flex-1">{items.map((s, i) => <span key={i} className="text-[11px] px-1.5 py-0.5 rounded" style={{ background: bg, color }}>{s.term}</span>)}</div>
                  </div>
                );
              })}
            </div>
          </CardContent></Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card><CardContent className="p-4">
            <div className="text-[11px] font-semibold text-muted-foreground mb-1.5">📑 Önerilen Plan</div>
            <div className="space-y-1">{b.outline.map((o, i) => (
              <div key={i} className="text-xs">
                <div className="font-medium">{o.h2}</div>
                {o.h3.length > 0 && <div className="pl-3 text-muted-foreground">{o.h3.map((h, j) => <div key={j}>– {h}</div>)}</div>}
              </div>
            ))}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4 space-y-3">
            <div><div className="text-[11px] font-semibold text-muted-foreground mb-1">🎯 Arama Niyeti</div><p className="text-xs text-muted-foreground">{b.searchIntent}</p></div>
            {b.cannibalizationCheck && (
              <div>
                <div className="text-[11px] font-semibold mb-1 text-success-600 dark:text-success-500">🛡️ Kanibalizasyon Kontrolü</div>
                <p className="text-xs text-muted-foreground">{b.cannibalizationCheck.note}{b.cannibalizationCheck.closestExisting && <> — en yakın mevcut sayfa: <a href={b.cannibalizationCheck.closestExisting} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted">{shortUrl(b.cannibalizationCheck.closestExisting)}</a></>}</p>
              </div>
            )}
            <div><div className="text-[11px] font-semibold mb-1" style={{ color: C.purple }}>🤖 AI/GEO Açısı</div><p className="text-xs text-muted-foreground">{b.aiAngle}</p></div>
            {b.internalLinkTargets.length > 0 && <div>
              <div className="text-[11px] font-semibold text-muted-foreground mb-1">🔗 Yayınlanınca Link Verilecek Sayfalar</div>
              <div className="space-y-0.5">{b.internalLinkTargets.map((l, i) => (
                <div key={i} className="text-xs truncate"><span style={{ color: C.green }}>"{l.anchor}"</span> → <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground underline decoration-dotted hover:text-cyan-400">{shortUrl(l.url)}</a></div>
              ))}</div>
            </div>}
          </CardContent></Card>
        </div>

        {/* ══ 14 BÖLÜMLÜ DERİN ANALİZ ══ */}
        <div className="mt-6">
          {!dd && canGenerateDeep && (
            <Card><CardContent className="p-6 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-semibold text-sm">🔬 Detaylı Analiz</div>
                <div className="text-xs text-muted-foreground mt-1">Başlık&Meta Boost · Rakip Trafiği · AI Manzarası · AI Overview cevabı · Güçlendirme Planı · Link uyarıları/fırsatları · Organik rakipler · Kelime fırsatları · Cluster bağlamı · GEO hedef promptlar + aksiyonlar (~150 token)</div>
                {deepErr && <div className="text-xs text-red-600 mt-1">{deepErr}</div>}
              </div>
              <Button onClick={startDeepDive} disabled={deepRunning} className="gap-2" style={{ background: C.purple }}>
                {deepRunning ? "Analiz sürüyor… (1-2 dk)" : "Detaylı Analiz Üret"}
              </Button>
            </CardContent></Card>
          )}

          {dd && (<div className="space-y-4">
            <div className="text-sm font-bold">🔬 Detaylı Analiz <span className="text-[11px] font-normal text-muted-foreground">· {new Date(dd.generatedAt).toLocaleString("tr-TR")} · ${dd.cost}</span></div>

            {dd.titleMetaBoost.length > 0 && <Card><CardContent className="p-4">
              <div className="text-xs font-semibold mb-2" style={{ color: C.purple }}>✨ Başlık & Meta Açıklama Boost</div>
              <div className="space-y-2.5">{dd.titleMetaBoost.map((t, i) => (
                <div key={i} className="text-xs border rounded-lg p-2.5">
                  <div className="font-semibold">{t.title}</div>
                  <div className="text-muted-foreground mt-0.5">{t.metaDescription}</div>
                  <div className="mt-1" style={{ color: C.purple }}>{t.angle}</div>
                </div>))}</div>
            </CardContent></Card>}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {dd.competitorTraffic.length > 0 && <Card><CardContent className="p-4">
                <div className="text-xs font-semibold mb-2" style={{ color: C.red }}>📈 Rakip Trafiği (SERP ilk 5)</div>
                {dd.competitorTraffic.map((c, i) => (
                  <div key={i} className="text-xs py-1 border-b last:border-0 flex justify-between gap-2">
                    <span className="truncate" title={c.url}>{c.domain}</span>
                    <span className="tabular-nums shrink-0">{fmt(c.traffic)}/ay · {fmt(c.keywords)} kelime</span>
                  </div>))}
              </CardContent></Card>}

              <Card><CardContent className="p-4">
                <div className="text-xs font-semibold mb-2" style={{ color: C.blue }}>🤖 Rekabet & AI Manzarası</div>
                <div className="text-xs">{dd.aiLandscape.note}</div>
                {dd.aiLandscape.aiSources.length > 0 && <div className="text-[11px] text-muted-foreground mt-1.5">AI kaynakları: {dd.aiLandscape.aiSources.join(", ")}</div>}
                {dd.aiOverviewAnswer.answer && <div className="text-[11px] mt-2 p-2 rounded bg-muted/50"><b>AI Overview şu anki cevap:</b> {dd.aiOverviewAnswer.answer.slice(0, 400)}{dd.aiOverviewAnswer.answer.length > 400 ? "…" : ""}</div>}
              </CardContent></Card>
            </div>

            {dd.strengthenPlan.length > 0 && <Card><CardContent className="p-4">
              <div className="text-xs font-semibold mb-2" style={{ color: C.amber }}>🎯 Güçlendirme Planı (öncelikli)</div>
              {dd.strengthenPlan.map((s, i) => (
                <div key={i} className="text-xs py-1.5 border-b last:border-0">
                  <span className="px-1.5 py-0.5 rounded text-[10px] mr-1.5" style={{ background: s.priority === "yüksek" ? `${C.red}18` : s.priority === "orta" ? `${C.amber}18` : `${C.green}18`, color: s.priority === "yüksek" ? C.red : s.priority === "orta" ? C.amber : C.green }}>{s.priority}</span>
                  <b>{s.action}</b> <span className="text-muted-foreground">— {s.why}</span>
                </div>))}
            </CardContent></Card>}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card><CardContent className="p-4">
                <div className="text-xs font-semibold mb-2" style={{ color: C.red }}>⚠️ Linkleme Uyarıları</div>
                {dd.linkWarnings.map((w, i) => <div key={i} className="text-xs py-1 border-b last:border-0">• {w}</div>)}
              </CardContent></Card>
              {dd.incomingLinkOpportunities.length > 0 && <Card><CardContent className="p-4">
                <div className="text-xs font-semibold mb-2" style={{ color: C.green }}>🔗 Link Building Fırsatları (gelen)</div>
                {dd.incomingLinkOpportunities.map((l, i) => (
                  <div key={i} className="text-xs py-1 border-b last:border-0 flex justify-between gap-2">
                    <span className="truncate" title={l.url}>{shortUrl(l.url)}</span>
                    <span className="shrink-0 tabular-nums" style={{ color: "#e83e3c" }}>ILR {l.ilr ?? "-"}</span>
                  </div>))}
                <div className="text-[10px] text-muted-foreground mt-1.5">Bu sayfalar yeni içeriğe GÖVDEDEN link vermeli (önerilen anchor: "{kw}").</div>
              </CardContent></Card>}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card><CardContent className="p-4">
                <div className="text-xs font-semibold mb-2">🔑 Anahtar Kelimeler</div>
                {dd.keywords.slice(0, 12).map((k, i) => <div key={i} className="text-xs py-0.5 flex justify-between gap-2"><span className="truncate">{k.keyword}</span><span className="tabular-nums text-muted-foreground shrink-0">{k.volume > 0 ? `${fmt(k.volume)}/ay` : k.role}</span></div>)}
              </CardContent></Card>
              {dd.organicCompetitors.length > 0 && <Card><CardContent className="p-4">
                <div className="text-xs font-semibold mb-2">🌍 Organik Rakipler ({(tekBrief?.market ?? "tr").toUpperCase()} pazarı)</div>
                {dd.organicCompetitors.map((c, i) => <div key={i} className="text-xs py-0.5 flex justify-between gap-2"><span className="truncate">{c.domain}</span><span className="tabular-nums text-muted-foreground shrink-0">{fmt(c.traffic)}/ay</span></div>)}
              </CardContent></Card>}
              {dd.keywordOpportunities.length > 0 && <Card><CardContent className="p-4">
                <div className="text-xs font-semibold mb-2">💎 Anahtar Kelime Fırsatları</div>
                {dd.keywordOpportunities.slice(0, 10).map((k, i) => <div key={i} className="text-xs py-0.5 flex justify-between gap-2"><span className="truncate">{k.keyword}</span><span className="tabular-nums text-muted-foreground shrink-0">{fmt(k.volume)}/ay · KD {k.difficulty}</span></div>)}
              </CardContent></Card>}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card><CardContent className="p-4">
                <div className="text-xs font-semibold mb-2">🗂 Cluster Bağlamı</div>
                <div className="text-xs">{dd.clusterContext.clusterName ?? "(cluster'sız)"} · {dd.clusterContext.pageCount} sayfa{dd.clusterContext.pillarUrl ? <> · pillar: <span className="text-muted-foreground">{shortUrl(dd.clusterContext.pillarUrl)}</span></> : ""}</div>
                {dd.clusterContext.memberSamples.map((m, i) => <div key={i} className="text-[11px] text-muted-foreground py-0.5 truncate">{shortUrl(m.url)} {m.title ? `— ${m.title.slice(0, 50)}` : ""}</div>)}
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <div className="text-xs font-semibold mb-2" style={{ color: C.purple }}>🧭 GEO Hedef Promptlar</div>
                {dd.geoTargetPrompts.slice(0, 10).map((q, i) => <div key={i} className="text-xs py-0.5">• {q}</div>)}
              </CardContent></Card>
            </div>

            {dd.promptActions.length > 0 && <Card><CardContent className="p-4">
              <div className="text-xs font-semibold mb-2" style={{ color: C.purple }}>🚀 Hedef Promptlarda Yer Alma Aksiyonları</div>
              {dd.promptActions.map((p, i) => (
                <div key={i} className="text-xs py-1.5 border-b last:border-0">
                  <div className="font-medium">"{p.prompt}"</div>
                  <div className="text-muted-foreground mt-0.5">→ {p.action}</div>
                </div>))}
            </CardContent></Card>}
          </div>)}
        </div>
      </>)}
    </div>
  );
}
